import { Decimal } from 'decimal.js'

import { CHART_COLORS } from '@/features/chart/lib/constants/chart-colors'
import {
  type LiquidationLevel,
  liquidationLevels,
  positionLegs,
} from '@/features/dashboard/lib/position-legs'
import {
  applyLivePositionMetrics,
  type LiveTicker,
} from '@/features/dashboard/lib/position-live-metrics'
import type { NumberFormatter } from '@/lib/number-format'
import type { TVPositionLine } from '@/lib/tradingview/types/tv-widget.types'
import { Denomination, OrderSide, Position } from '@/types/generated/common/trading'

export interface PositionInfo {
  /** Position quantity in the server-native size denomination. */
  quantity: number
  sizeDenomination: Denomination
  baseQty: number
  netQty: number
  avgEntry: number
  side: 'long' | 'short'
  /** Server-computed mark price at the time positionFromAtom was called */
  markPrice?: number
  /** Position-table PnL value at markPrice */
  serverPnl?: number
  /** Position-table PnL percent */
  serverPnlPct?: number
  /** Venue-reported estimated liquidation price, passed through as-is */
  liquidationPrice?: number
  /** One per liquidation line when the venue reports sub-positions, nearest the mark first. */
  liquidationLevels?: LiquidationLevel[]
}

export interface PnLResult {
  rawPnl: number
  pnlPct: number
  lineColor: string
}

function relativeDistance(a: Decimal, b: Decimal): Decimal | null {
  if (!a.isFinite() || !b.isFinite() || a.lte(0) || b.lte(0)) return null
  return a.minus(b).abs().div(Decimal.max(a, b))
}

function inferSizeDenomination(pos: Position, quantity: Decimal): Denomination {
  const nativeDenomination =
    pos.sizeDenomination === Denomination.DENOMINATION_QUOTE
      ? Denomination.DENOMINATION_QUOTE
      : Denomination.DENOMINATION_BASE
  const delta = new Decimal(pos.delta || '0').abs()
  const deltaUsd = new Decimal(pos.deltaUsd || '0').abs()
  const quantityAbs = quantity.abs()
  const baseDistance = relativeDistance(quantityAbs, delta)
  const quoteDistance = relativeDistance(quantityAbs, deltaUsd)

  if (
    quoteDistance &&
    quoteDistance.lte(0.05) &&
    (!baseDistance || quoteDistance.lt(baseDistance))
  ) {
    return Denomination.DENOMINATION_QUOTE
  }
  if (
    baseDistance &&
    baseDistance.lte(0.05) &&
    (!quoteDistance || baseDistance.lt(quoteDistance))
  ) {
    return Denomination.DENOMINATION_BASE
  }

  return nativeDenomination
}

export function positionDisplaySize(position: PositionInfo, unit: 'base' | 'quote'): number {
  if (unit === 'base') return position.baseQty
  return position.sizeDenomination === Denomination.DENOMINATION_QUOTE
    ? position.quantity
    : new Decimal(position.baseQty).mul(position.markPrice ?? position.avgEntry).toNumber()
}

interface ApplyPositionLineAppearanceOptions {
  baseCurrency?: string
  positionSizeUnit?: 'base' | 'quote'

  includeStaticProps?: boolean
  accountLabel?: string
  /** Masks the $ PnL while balances are hidden (PnL % stays visible). */
  fmt?: NumberFormatter
}

export function applyPositionLineAppearance(
  line: TVPositionLine,
  position: PositionInfo,
  pnl: PnLResult,
  options: ApplyPositionLineAppearanceOptions = {}
): TVPositionLine {
  const { includeStaticProps = false, accountLabel, fmt } = options

  if (includeStaticProps) {
    line.setPrice(position.avgEntry).setLineStyle(0).setLineWidth(1)
  }

  const size = positionDisplaySize(position, options.positionSizeUnit ?? 'base')
  const sizeLabel = fmt
    ? fmt.number(size, { sensitive: true, maximumSignificantDigits: 6 })
    : String(size)
  const unitLabel =
    (options.positionSizeUnit ?? 'base') === 'base' ? (options.baseCurrency ?? '') : 'USD'
  const sideLabel = position.side === 'long' ? 'L' : 'S'
  const rawPnlLabel = `${pnl.rawPnl >= 0 ? '+' : ''}${pnl.rawPnl.toFixed(2)}`
  const pnlLabel = fmt ? fmt.text(rawPnlLabel, { sensitive: true }) : rawPnlLabel
  const pctLabel =
    position.serverPnlPct === undefined
      ? ''
      : ` ${position.serverPnlPct >= 0 ? '+' : ''}${position.serverPnlPct.toFixed(2)}%`

  // A dark gray body keeps white text legible on both light and dark chart themes.
  return line
    .setQuantity(`${sizeLabel} ${unitLabel}`)
    .setQuantityBackgroundColor(CHART_COLORS.positionLabelBackground)
    .setQuantityBorderColor(CHART_COLORS.positionLabelBackground)
    .setQuantityTextColor(CHART_COLORS.markLabel)
    .setText(`${accountLabel ? `${accountLabel} · ` : ''}${sideLabel} ${pnlLabel}${pctLabel}`)
    .setBodyTextColor(CHART_COLORS.markLabel)
    .setBodyBorderColor(CHART_COLORS.positionLabelBackground)
    .setBodyBackgroundColor(CHART_COLORS.positionLabelBackground)
    .setLineColor(CHART_COLORS.neutral)
}

export function calculatePnL(position: PositionInfo, currentPrice: number): PnLResult {
  try {
    const { markPrice, serverPnl, avgEntry, baseQty, side } = position
    const currentPriceDec = new Decimal(currentPrice)
    const avgEntryDec = new Decimal(avgEntry)
    const baseQtyDec = new Decimal(baseQty)

    let rawPnl: Decimal

    if (
      serverPnl !== undefined &&
      markPrice !== undefined &&
      !new Decimal(markPrice).equals(avgEntryDec)
    ) {
      const markPriceDec = new Decimal(markPrice)
      const serverPnlDec = new Decimal(serverPnl)
      // rawPnl = serverPnl * ((currentPrice - avgEntry) / (markPrice - avgEntry))
      rawPnl = serverPnlDec.mul(
        currentPriceDec.minus(avgEntryDec).div(markPriceDec.minus(avgEntryDec))
      )
    } else if (serverPnl !== undefined) {
      rawPnl = new Decimal(serverPnl)
    } else {
      // Fallback (spot / unknown contracts)
      if (side === 'long') {
        rawPnl = currentPriceDec.minus(avgEntryDec).mul(baseQtyDec)
      } else {
        rawPnl = avgEntryDec.minus(currentPriceDec).mul(baseQtyDec)
      }
    }

    const pnlPct =
      side === 'long'
        ? currentPriceDec.minus(avgEntryDec).div(avgEntryDec).mul(100)
        : avgEntryDec.minus(currentPriceDec).div(avgEntryDec).mul(100)

    const rawPnlNum = rawPnl.toNumber()
    const lineColor = rawPnlNum >= 0 ? CHART_COLORS.profit : CHART_COLORS.loss

    return { rawPnl: rawPnlNum, pnlPct: pnlPct.toNumber(), lineColor }
  } catch (e) {
    return { rawPnl: 0, pnlPct: 0, lineColor: CHART_COLORS.neutral }
  }
}

/**
 * Converts a Position atom object to PositionInfo for chart overlay use.
 */
export function positionFromAtom(pos: Position, liveTicker?: LiveTicker): PositionInfo | null {
  try {
    const displayPosition = applyLivePositionMetrics(pos, liveTicker)
    const quantity = new Decimal(displayPosition.quantity)
    const avgEntry = new Decimal(displayPosition.avgPrice)
    if (!quantity.isFinite() || !avgEntry.isFinite() || quantity.lte(0) || avgEntry.lte(0))
      return null

    const markPrice = new Decimal(displayPosition.markPrice || displayPosition.avgPrice)
    const serverPnl = new Decimal(
      displayPosition.unrealizedPnlBase ?? displayPosition.unrealizedPnl ?? '0'
    )
    const serverPnlPct = displayPosition.pnlPercent ? new Decimal(displayPosition.pnlPercent) : null
    const liquidationPrice = displayPosition.liquidationPrice
      ? new Decimal(displayPosition.liquidationPrice)
      : null
    const conversionPrice = markPrice.isFinite() && markPrice.gt(0) ? markPrice : avgEntry
    const levels = liquidationLevels(positionLegs(pos), conversionPrice.toNumber())
    const sizeDenomination = inferSizeDenomination(displayPosition, quantity)
    const baseQty =
      sizeDenomination === Denomination.DENOMINATION_QUOTE
        ? quantity.div(conversionPrice)
        : quantity

    return {
      quantity: quantity.toNumber(),
      sizeDenomination,
      baseQty: baseQty.toNumber(),
      netQty: quantity.toNumber(),
      avgEntry: avgEntry.toNumber(),
      side: displayPosition.side === OrderSide.ORDER_SIDE_BUY ? 'long' : 'short',
      markPrice: markPrice.isFinite() ? markPrice.toNumber() : undefined,
      serverPnl: serverPnl.isFinite() ? serverPnl.toNumber() : undefined,
      serverPnlPct: serverPnlPct && serverPnlPct.isFinite() ? serverPnlPct.toNumber() : undefined,
      liquidationPrice:
        liquidationPrice && liquidationPrice.isFinite() && liquidationPrice.gt(0)
          ? liquidationPrice.toNumber()
          : undefined,
      // Legs with no liquidation price fall back to the position's own line.
      liquidationLevels: levels.length ? levels : undefined,
    }
  } catch (e) {
    return null
  }
}
